import * as R from 'ramda';
import { ActionType } from '../redux/actionType';
import { getLoading, getError } from './module';
import { useAppState } from '../redux/hooks';
import { ApiError } from 'redux-api-middleware';
import { ApiLoading } from './apiLoading';
import { Map } from '../../language/models';


/**
 * Returns the loading state from AppState for the given action type and request ID
 */
export const useLoading = (
  actionType: ActionType,
  requestId: string | number | undefined = undefined,
): boolean => useAppState(state => !!getLoading(state, actionType, requestId));


/**
 * Returns whether or not any of the given action types have a loading status in app state
 */
export const useAnyLoading = (
  actionTypes: ActionType[],
): boolean => useAppState(state => R.any(action => !!getLoading(state, action), actionTypes));


/**
 * Returns all loading states of the given action type (including those with a request Id which contains more
 * information than the action type itself).
 *
 * @param actionType - the type of actions to filter
 */
export const useLoadingOfType = (
  actionType: ActionType,
): Map<ApiLoading> => {
  const loadingMap = useAppState(state => state.loading);
  return R.pickBy((value, key) => key.startsWith(actionType), loadingMap);
};


/**
 * Returns the error from AppState for the given action type and request ID (if an error exists)
 */
export const useError = (
  actionType: ActionType,
  requestId: string | number | undefined = undefined,
): string | undefined => useAppState(state => getError(state, actionType, requestId)?.errorMsg);


/**
 * Returns whether or not any of the given action types have an error in app state
 */
export const useAnyError = (
  actionTypes: ActionType[],
): boolean => useAppState(state => R.any(action => !!getError(state, action), actionTypes));


/**
 * Returns all error states of the given action type (including those with a request Id which contains more
 * information than the action type itself).
 *
 * @param actionType - the type of actions to filter
 */
export const useErrorOfType = (
  actionType: ActionType,
): Map<ApiError> => {
  const errorMap = useAppState(state => state.error);
  return R.pickBy((value, key) => key.startsWith(actionType), errorMap);
};
